/**
 * Who is signed in, asked ONCE per page load.
 *
 * TASK-042 §4: `GET /v1/auth/me` answers 200 whether or not there is a
 * session, returns `user.id`, and had two callers each with its own idea of
 * the answer's shape. Two callers of one endpoint is how the second one ends
 * up reading a field the first one renamed, so there is one module and both
 * call it.
 *
 * It exists because three preferences on this screen are three different
 * scopes and were being treated as one:
 *
 *   divider width      the DEVICE -- unkeyed, a width belongs to a screen
 *   tree expand state  the PERSON -- keyed, it names folder ids
 *   list / icons       the PERSON -- keyed
 *   the folder tree    the PERSON -- keyed, it names folders
 *
 * Anything keyed by person is keyed by `user.id` through `scopedKey` here, so
 * two people sharing a browser profile never read each other's entry.
 *
 * The promise is memoised, not the value: a second caller arriving while the
 * first request is in flight awaits that same request rather than making a
 * second one. A network failure resolves to `null` and is NOT cached, so the
 * next caller tries again -- a dropped connection at boot must not leave the
 * tab permanently convinced it is nobody.
 */

export interface MeUser {
  id: string
  email: string | null
  displayName: string | null
  orgId: string | null
  agentId: string | null
}

export interface Me {
  authenticated: boolean
  user: MeUser | null
  providers?: Record<string, unknown>
  /** The raw body, for callers that read fields this shape does not name. */
  raw: any
}

let pending: Promise<Me | null> | null = null

async function ask(): Promise<Me | null> {
  let res: Response
  try {
    // The session cookie is the credential and rides along on a same-origin
    // fetch; `api()` is not used because a 200 saying "nobody" is a successful
    // answer to this question and must not go through the refusal path.
    res = await fetch('/v1/auth/me', {
      credentials: 'same-origin',
      headers: { accept: 'application/json' },
    })
  } catch {
    return null
  }
  const body = await res.json().catch(() => null)
  if (!body || typeof body !== 'object') return null
  const user = body.user && typeof body.user === 'object' ? body.user : null
  return {
    authenticated: body.authenticated === true,
    user: user && typeof user.id === 'string' ? (user as MeUser) : null,
    providers: body.providers,
    raw: body,
  }
}

export function me(): Promise<Me | null> {
  if (!pending) {
    pending = ask().then((value) => {
      // Only a real answer is remembered. A failure leaves the slot empty so
      // the next caller asks again.
      if (value === null) pending = null
      return value
    })
  }
  return pending
}

/** The signed-in user's id, or `null` if we are not signed in or cannot tell. */
export async function myUserId(): Promise<string | null> {
  return (await me())?.user?.id ?? null
}

/**
 * A `localStorage` key scoped to a person.
 *
 * `null` -- nobody, or not known yet -- gets its own bucket rather than the
 * unkeyed one, so an anonymous visit cannot overwrite a signed-in person's
 * entry and a signed-in person cannot inherit an anonymous one.
 */
export function scopedKey(base: string, userId: string | null): string {
  return `${base}:${userId ?? 'anon'}`
}

/**
 * Every `localStorage` access on this screen goes through these two.
 *
 * Private mode, a blocked origin and a full quota all throw, and none of them
 * is worth a blank screen: a preference that does not survive a reload is a
 * smaller failure than a page that does not render.
 */
export function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

export function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* see readStored */
  }
}

export function dropStored(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    /* see readStored */
  }
}
